import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Upload, MessageCircle, CheckCircle2, ArrowLeft, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { copyText } from "@/pages/landing/Checkout";
import { CheckingCard, isChecking, useLang } from "./FreeCards";

const U = {
  en: {
    copied: "Copied!", noCopy: "Could not copy. Please copy it by hand.", fileErr: "Please choose a JPG, PNG or PDF file (max 10 MB).", upload: "Upload Payment Proof",
    sending: "Sending…", submit: "Submit My Payment", sendWa: "Send Proof on WhatsApp", waHint: "In WhatsApp, add your payment screenshot or receipt, then press send.",
    waMsg: (p, n, e) => `Hello Sidebizz, I have paid Rs ${p} to unlock all 150+ business ideas.\nMy name: ${n}\nMy account email: ${e}\nHere is my payment proof.`,
    recWaT: "Thanks! Now send your proof on WhatsApp", recT: "Payment Proof Received!",
    recWaD: "After you send the message with your screenshot, go back to your dashboard. We’re checking your payment and will unlock all ideas once it is confirmed.",
    recD: "Thank you! We will check your payment and activate your full access once payment is confirmed.", backDash: "Back to My Dashboard", dash: "My dashboard",
    title: "Unlock all ideas", step1: "Make your payment", amount: "Amount", company: "Company name", juice: "Juice number", copyJuice: "Copy Juice Number", bank: "Bank",
    account: "Bank account number", copyAccount: "Copy Bank Account Number", step2: "Send your payment proof", optA: "OPTION A", optB: "OPTION B", formats: "JPG, PNG or PDF",
  },
  fr: {
    copied: "Copié !", noCopy: "Impossible de copier. Copie-le à la main.", fileErr: "Choisis un fichier JPG, PNG ou PDF (10 Mo max).", upload: "Envoyer ma preuve de paiement",
    sending: "Envoi…", submit: "Envoyer mon paiement", sendWa: "Envoyer la preuve sur WhatsApp", waHint: "Dans WhatsApp, ajoute ta capture d’écran ou ton reçu, puis appuie sur envoyer.",
    waMsg: (p, n, e) => `Bonjour Sidebizz, j’ai payé Rs ${p} pour débloquer les 150+ idées de business.\nMon nom : ${n}\nL’email de mon compte : ${e}\nVoici ma preuve de paiement.`,
    recWaT: "Merci ! Envoie maintenant ta preuve sur WhatsApp", recT: "Preuve de paiement reçue !",
    recWaD: "Après avoir envoyé le message avec ta capture, reviens sur ton tableau de bord. Nous vérifions ton paiement et nous débloquons toutes les idées dès qu’il est confirmé.",
    recD: "Merci ! Nous vérifions ton paiement et nous activons ton accès complet dès qu’il est confirmé.", backDash: "Retour à mon tableau de bord", dash: "Mon tableau de bord",
    title: "Débloquer toutes les idées", step1: "Fais ton paiement", amount: "Montant", company: "Nom de la société", juice: "Numéro Juice", copyJuice: "Copier le numéro Juice", bank: "Banque",
    account: "Numéro de compte bancaire", copyAccount: "Copier le numéro de compte", step2: "Envoie ta preuve de paiement", optA: "OPTION A", optB: "OPTION B", formats: "JPG, PNG ou PDF",
  },
};
const useU = () => U[useLang()];

export function CopyRow({ label, value, btn, k }) {
  const u = useU();
  const [ok, setOk] = useState(false);
  const copy = async () => { if (await copyText(value)) { setOk(true); toast.success(u.copied); setTimeout(() => setOk(false), 1800); } else toast.error(u.noCopy); };
  return (
    <div className="py-3 border-t border-[#E1ECEF] first:border-0">
      <div className="text-xs font-semibold text-[#7D8C96]">{label}</div>
      <div className="font-bold text-lg text-[#102B3F] break-all select-all" data-testid={`unlock-${k}`}>{value}</div>
      {btn && <button onClick={copy} className={`mt-2 w-full h-11 rounded-xl font-bold text-sm inline-flex items-center justify-center gap-2 border ${ok ? "bg-[#0BAF9A] border-[#0BAF9A] text-white" : "border-[#102B3F] text-[#102B3F]"}`} data-testid={`unlock-copy-${k}`}>{ok ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}{ok ? u.copied : btn}</button>}
    </div>
  );
}

export const StepTitle = ({ n, children }) => <h2 className="font-display font-extrabold text-xl text-[#102B3F] flex items-center gap-2.5"><span className="h-8 w-8 rounded-full bg-[#0BAF9A] text-white text-sm flex items-center justify-center">{n}</span>{children}</h2>;

export function UploadBox({ onDone, endpoint = "/free/payment-request/proof", extra = {} }) {
  const u = useU();
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const pick = (f) => {
    if (!f) return;
    if (!/\.(jpe?g|png|pdf)$/i.test(f.name) || f.size > 10 * 1024 * 1024) { toast.error(u.fileErr); return; }
    setFile({ f, url: f.type.startsWith("image/") ? URL.createObjectURL(f) : null });
  };
  const submit = async () => {
    if (busy || !file) return;
    const fd = new FormData(); fd.append("file", file.f);
    Object.entries(extra).forEach(([k, v]) => v != null && fd.append(k, v));
    setBusy(true);
    try { await api.post(endpoint, fd); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="unlock-upload-box">
      <input ref={ref} type="file" accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="unlock-file-input" />
      <button onClick={() => ref.current?.click()} className="w-full h-12 rounded-xl border-2 border-dashed border-[#0BAF9A] text-[#102B3F] font-bold inline-flex items-center justify-center gap-2" data-testid="unlock-upload-btn"><Upload className="h-5 w-5" />{u.upload}</button>
      <p className="text-xs text-[#7D8C96] text-center">{u.formats}</p>
      {file && (
        <div className="rounded-2xl bg-[#F4F8F9] p-3 flex items-center gap-3" data-testid="unlock-file-preview">
          {file.url ? <img src={file.url} alt="" className="h-16 w-16 rounded-xl object-cover" /> : <FileText className="h-10 w-10 text-[#0BAF9A]" />}
          <span className="text-sm font-semibold text-[#102B3F] break-all">{file.f.name}</span>
        </div>
      )}
      <button onClick={submit} disabled={!file || busy} className="lp-btn w-full disabled:opacity-40" data-testid="unlock-submit-btn">{busy ? u.sending : u.submit}</button>
    </div>
  );
}

export function WhatsAppBox({ data, onSent, message, endpoint = "/free/payment-request/whatsapp", label, hint }) {
  const { user } = useAuth();
  const u = useU();
  const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  const msg = message || u.waMsg(data.price, name, user.email);
  const href = `https://wa.me/${data.payment.proof_whatsapp_number}?text=${encodeURIComponent(msg)}`;
  const click = () => { api.post(endpoint).then(onSent).catch((e) => toast.error(errMsg(e))); };
  return (
    <div className="space-y-2">
      <a href={href} target="_blank" rel="noreferrer" onClick={click} className="w-full h-12 rounded-xl bg-[#25D366] hover:bg-[#1EBE5A] text-white font-bold inline-flex items-center justify-center gap-2" data-testid="unlock-whatsapp-btn"><MessageCircle className="h-5 w-5" />{label || u.sendWa}</a>
      <p className="text-xs text-[#7D8C96] text-center">{hint || u.waHint}</p>
    </div>
  );
}

export const Received = ({ whatsapp }) => { const u = useU(); return (
  <div className="rounded-3xl bg-white border border-[#E1ECEF] p-6 text-center space-y-3" data-testid="unlock-received">
    <CheckCircle2 className="h-14 w-14 text-[#0BAF9A] mx-auto" />
    <h1 className="font-display font-extrabold text-2xl text-[#102B3F]">{whatsapp ? u.recWaT : u.recT}</h1>
    <p className="text-[15px] text-[#4A5D6A]">{whatsapp ? u.recWaD : u.recD}</p>
    <Link to="/" className="lp-btn w-full" data-testid="unlock-back-dashboard-btn">{u.backDash}</Link>
  </div>
); };

export default function Unlock({ data, reload }) {
  const u = useU();
  const [done, setDone] = useState(null);
  const p = data.payment;
  const finish = (kind) => { setDone(kind); reload(); };
  if (done) return <Received whatsapp={done === "wa"} />;
  if (isChecking(data.request)) return <div className="space-y-4"><CheckingCard /><Link to="/" className="lp-btn w-full" data-testid="unlock-back-dashboard-btn">{u.backDash}</Link></div>;
  return (
    <div className="space-y-5" data-testid="unlock-page">
      <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-[#5C6B73]"><ArrowLeft className="h-4 w-4" />{u.dash}</Link>
      <h1 className="font-display font-extrabold text-3xl text-[#102B3F]">{u.title}</h1>
      <section className="rounded-3xl bg-white border border-[#E1ECEF] p-5 space-y-3">
        <StepTitle n={1}>{u.step1}</StepTitle>
        <div className="rounded-2xl bg-[#102B3F] text-white p-4 flex items-center justify-between"><span className="text-white/70">{u.amount}</span><span className="font-display font-extrabold text-2xl" data-testid="unlock-amount">Rs {data.price}</span></div>
        <div>
          <CopyRow k="company" label={u.company} value={p.company_name} />
          <CopyRow k="juice" label={u.juice} value={p.juice_number} btn={u.copyJuice} />
          <CopyRow k="bank" label={u.bank} value={p.bank_name} />
          <CopyRow k="account" label={u.account} value={p.bank_account} btn={u.copyAccount} />
        </div>
      </section>
      <section className="rounded-3xl bg-white border border-[#E1ECEF] p-5 space-y-4">
        <StepTitle n={2}>{u.step2}</StepTitle>
        <div><div className="text-xs font-bold tracking-widest text-[#088F7E] mb-2">{u.optA}</div><UploadBox onDone={() => finish("upload")} /></div>
        <div className="border-t border-[#E1ECEF] pt-4"><div className="text-xs font-bold tracking-widest text-[#088F7E] mb-2">{u.optB}</div><WhatsAppBox data={data} onSent={() => finish("wa")} /></div>
      </section>
    </div>
  );
}
